import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Button, Typography, IconButton, Tooltip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel,
  Stack, Chip, Drawer, Divider, TextField, CircularProgress, LinearProgress, Checkbox,
  InputAdornment, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  ReportProblem as WarnIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  StickyNote2 as NoteIcon,
  StickyNote2Outlined as NoteOutlinedIcon,
} from '@mui/icons-material';
import useSortableTable from '../utils/useSortableTable';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import PipelineProgress from '../components/PipelineProgress';
import SourcesOnboardingCallout from '../components/SourcesOnboardingCallout';
import { fetchAvailableFiles } from '../store/slices/jobsSlice';
import { fetchDCPAvailableFiles } from '../store/slices/dcpJobsSlice';
import { useUploadManager } from '../contexts/UploadManagerContext';
import BulkActionBar from '../components/BulkActionBar';
import { useBulkRunner } from '../utils/bulkOps';
import {
  COMPLIANCE_STATUS_MAP,
  complianceChipColor,
  shouldShowFfecSection,
  countDoneTasks,
  isGuideDismissed,
  setGuideDismissed,
  computeSourceAggregates,
  formatBytes,
  canCreateForProduct,
  filterSources,
  shouldShowBulkOps,
  isBulkOpsForced,
  setBulkOpsForced,
  qcVerdictWarning,
} from '../utils/sources';
import { pollQcReport } from '../utils/qcPolling';

// Local table/row formatter — returns "-" for missing/zero (cleaner
// in a table column than "0 B"). Wraps the shared formatBytes for
// the actual unit math so the two stay in sync.
function fmtBytes(b) {
  if (!b || b < 0) return '-';
  return formatBytes(b);
}

function fmtDate(ts) {
  if (!ts) return '-';
  return new Date(ts * 1000).toLocaleString();
}

export default function Sources() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { availableFiles } = useSelector((state) => state.jobs);
  const activeProduct = useSelector((state) => state.company.activeProduct);
  const activeCompanyId = useSelector((state) => state.company.activeCompanyId);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const isSuperadmin = !!userInfo?.is_superadmin;
  const { rows: rawSortedFiles, sortProps } = useSortableTable(
    availableFiles || [],
    'filename',
    'asc',
    {
      filename: (r) => (r.filename || '').toLowerCase(),
      qc_compliance: (r) => { const order = { fail: 0, needs_input: 1, warn: 2, pass: 3 }; return order[r.qc_compliance] ?? 4; },
      size: (r) => r.size || 0,
      modified: (r) => r.modified ? new Date(r.modified).getTime() : 0,
    },
  );
  // Best-marked sources rise to the top regardless of the active sort,
  // preserving relative order within each group. Operator-marked
  // canonical cuts then act like a "pinned" row.
  const sortedFiles = [
    ...rawSortedFiles.filter((f) => f.is_best),
    ...rawSortedFiles.filter((f) => !f.is_best),
  ];
  // Filter state. Operator-facing controls live in a small toolbar
  // above the table; the aggregate-stats row keeps the workspace
  // totals (filtering changes the visible rows, not what they own).
  const [searchTerm, setSearchTerm] = useState('');
  const [complianceFilter, setComplianceFilter] = useState('all');
  const [availableForFilter, setAvailableForFilter] = useState('all');
  const filteredFiles = filterSources(sortedFiles, {
    search: searchTerm,
    compliance: complianceFilter,
    availableFor: availableForFilter,
  });
  const filtersActive = !!searchTerm
    || complianceFilter !== 'all'
    || availableForFilter !== 'all';
  const clearFilters = () => {
    setSearchTerm('');
    setComplianceFilter('all');
    setAvailableForFilter('all');
  };

  // Bulk operations. Visibility is gated by both the workspace size
  // and (for superadmins) a localStorage override they can toggle to
  // QC the bulk UI on a small fixture. `selectedFilenames` is the
  // Set of filenames currently checked; cleared when the underlying
  // list changes (e.g. a bulk delete just succeeded).
  const [bulkForce, setBulkForce] = useState(isBulkOpsForced());
  const bulkVisible = shouldShowBulkOps(sortedFiles.length, isSuperadmin, bulkForce);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const toggleBulkForce = () => {
    const next = !bulkForce;
    setBulkForce(next);
    setBulkOpsForced(next);
  };
  // Shared runner — handles selection state, concurrency, and progress.
  const bulk = useBulkRunner({
    onComplete: () => refresh(),
    onError: ({ failed, total }) =>
      setError(`${failed} of ${total} bulk operations failed. Try again.`),
  });
  const visibleIds = filteredFiles.map((f) => f.filename);
  const selectedFilenames = bulk.selectedIds;

  const doBulkDelete = async () => {
    setBulkDeleteOpen(false);
    await bulk.run((filename) =>
      api.delete(`/api/jobs/available-files/${encodeURIComponent(filename)}`),
    );
  };
  const doBulkRerunQc = () => bulk.run((filename) =>
    api.post(`/api/jobs/available-files/${encodeURIComponent(filename)}/qc-rerun`),
  );
  const doBulkToggleFlag = (product, value) => bulk.run((filename) =>
    api.patch(`/api/jobs/available-files/${encodeURIComponent(filename)}/flags`, { [product]: value }),
  );

  const [qcDrawer, setQcDrawer] = useState(null);
  const [manualMarkers, setManualMarkers] = useState({ ffec_timecode: '', ffmc_timecode: '' });
  const [uploadGuide, setUploadGuide] = useState(false);
  const [uploadConfirmed, setUploadConfirmed] = useState({ master: false, codec: false, audio: false, time: false });
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const allConfirmed = Object.values(uploadConfirmed).every(Boolean);
  const triggerFilePicker = () => {
    if (dontShowAgain) setGuideDismissed(true);
    setUploadGuide(false);
    setUploadConfirmed({ master: false, codec: false, audio: false, time: false });
    setTimeout(() => document.getElementById('source-upload')?.click(), 0);
  };
  // The Upload button's behavior depends on whether the operator has
  // dismissed the guide. First time → dialog. Returning operator →
  // straight to the file picker. The guide is still reachable from
  // the "Show upload guide" link in the header for re-reads.
  const handleUploadClick = () => {
    if (isGuideDismissed()) {
      document.getElementById('source-upload')?.click();
    } else {
      setUploadGuide(true);
    }
  };
  const reopenGuide = () => {
    setUploadConfirmed({ master: false, codec: false, audio: false, time: false });
    setDontShowAgain(false);
    setUploadGuide(true);
  };

  const refreshSourceLists = () => {
    dispatch(fetchAvailableFiles());
    dispatch(fetchDCPAvailableFiles());
  };

  const openQc = async (f) => {
    setQcDrawer({ filename: f.filename, loading: true });
    try {
      const { data } = await api.get(`/api/jobs/available-files/${encodeURIComponent(f.filename)}/qc`);
      // If QC is currently running, poll until it finishes — otherwise
      // the drawer reads "Compliance: UNKNOWN" until manually reopened.
      // Poll-loop body is shared with rerunQc via utils/qcPolling.js
      // (extracted v10.4.34).
      setQcDrawer({ filename: f.filename, report: data.report, running: !!data.running });
      if (data.running) {
        const final = await pollQcReport(api, f.filename, {
          onTick: (poll) => {
            if (poll?.report) {
              setQcDrawer({ filename: f.filename, report: poll.report, running: true });
            }
          },
        });
        if (final) {
          setQcDrawer({ filename: f.filename, report: final.report });
          refreshSourceLists();
        } else {
          setQcDrawer((prev) => prev
            ? { ...prev, running: false, error: 'QC is still running — re-open the drawer to check again.' }
            : prev);
        }
      }
    } catch (_e) {
      setQcDrawer({ filename: f.filename, error: 'Failed to load report' });
    }
  };
  const rerunQc = async (f) => {
    setQcDrawer({ filename: f.filename, loading: true, error: null, running: true });
    try {
      // Fire-and-forget rerun — backend dedupes by source path, so
      // rapid repeat clicks don't spawn parallel scans. Then poll
      // until done via the shared helper.
      await api.post(`/api/jobs/available-files/${encodeURIComponent(f.filename)}/qc-rerun`);
      const final = await pollQcReport(api, f.filename, {
        onTick: (poll) => {
          if (poll?.report) {
            setQcDrawer({ filename: f.filename, report: poll.report, running: true });
          }
        },
      });
      if (final) {
        setQcDrawer({ filename: f.filename, report: final.report });
        refreshSourceLists();
      } else {
        setQcDrawer({ filename: f.filename, loading: false, error: 'QC is still running. Refresh the page to check again.' });
      }
    } catch (_e) {
      setQcDrawer({ filename: f.filename, error: 'Compliance check failed' });
    }
  };
  const submitManualMarkers = async () => {
    if (!qcDrawer?.filename) return;
    try {
      await api.post(`/api/jobs/available-files/${encodeURIComponent(qcDrawer.filename)}/markers`, manualMarkers);
      const { data } = await api.get(`/api/jobs/available-files/${encodeURIComponent(qcDrawer.filename)}/qc`);
      setQcDrawer({ filename: qcDrawer.filename, report: data.report });
      setManualMarkers({ ffec_timecode: '', ffmc_timecode: '' });
      dispatch(fetchAvailableFiles());
    } catch (_e) { /* show in drawer */ }
  };
  // (Dropped `chipColor = complianceChipColor` alias in v10.4.34 —
  // call sites use complianceChipColor directly now.)
  const { enqueue, tasks } = useUploadManager();
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const refresh = () => {
    dispatch(fetchAvailableFiles());
    dispatch(fetchDCPAvailableFiles());
  };

  // Refire when activeCompanyId arrives so the page recovers from the
  // mount race where Sources loaded before the company-context handshake
  // completed. Without this dep, the initial fetch goes out without
  // X-Company-Id (canceled at the request interceptor in v10.12.2+),
  // and the page never refetches — operator sees "no sources" forever.
  useEffect(() => { if (activeCompanyId) refresh(); }, [activeCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh whenever any upload completes — even ones started from
  // the Create pages, not just from this page. Without this the list
  // stays stale until the operator hits Refresh, and a freshly-uploaded
  // source is invisible despite being on disk and being QC'd.
  const doneCount = countDoneTasks(tasks);
  const lastDoneRef = useRef(doneCount);
  useEffect(() => {
    if (doneCount > lastDoneRef.current) {
      lastDoneRef.current = doneCount;
      // Slight delay so the post-finish hook has time to write the
      // .source-flags.json sidecar before we re-fetch.
      setTimeout(refresh, 800);
    }
  }, [doneCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sniff results awaiting user confirmation: { file, sniff }
  const [sniffPending, setSniffPending] = useState(null);
  const [sniffing, setSniffing] = useState(false);

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;
    // Process one at a time so the user can review each result
    for (const file of files) {
      try {
        setSniffing(true);
        const { sniffSource } = await import('../services/sourceUpload');
        const sniff = await sniffSource(file);
        setSniffing(false);
        if (sniff.compliance === 'fail') {
          // Hard reject, show the report and do NOT enqueue
          setSniffPending({ file, sniff, blocked: true });
          break;
        }
        // For warn/pass, surface the report and let the user confirm
        await new Promise((resolve) => {
          setSniffPending({
            file, sniff, blocked: false,
            confirm: () => {
              enqueue(file, {
                // "auto" lets the post-finish hook set per-product
                // availability from the QC verdict — pass → on, anything
                // else → off. Operator can flip them manually from the
                // row toggles after upload completes.
                flags: { watermarks: 'auto', dcp: 'auto' },
                onComplete: () => setTimeout(refresh, 1000),
              });
              setSniffPending(null);
              resolve();
            },
            cancel: () => { setSniffPending(null); resolve(); },
          });
        });
      } catch (e) {
        setSniffing(false);
        setError(`Pre-flight check failed: ${e?.message || e}`);
      }
    }
  };

  const updateFlags = async (file, patch) => {
    setError(null);
    try {
      await api.patch(
        `/api/jobs/available-files/${encodeURIComponent(file.filename)}/flags`,
        patch,
      );
      refresh();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  // Toggle the operator-marked "best source" flag. Used as a visual
  // cue + sort key when the workspace has multiple cuts of the same
  // picture and the operator wants to pin the canonical one to the
  // top of the list.
  const toggleBest = async (file) => {
    await updateFlags(file, { is_best: !file.is_best });
  };

  // Available For guardrail. When QC said `fail` (or `needs_input`
  // for DCP) and the operator tries to flip the switch back ON, we
  // pause for a confirmation. If they confirm, the switch flips and
  // the row will encode despite QC's warning. The off→off and on→off
  // directions never confirm — turning a source OFF is always
  // operator intent.
  const [availabilityConfirm, setAvailabilityConfirm] = useState(null);
  const requestToggleAvailability = (file, product, nextValue) => {
    if (!nextValue) {
      // Going off — no guardrail needed.
      updateFlags(file, { [product]: false });
      return;
    }
    const warning = qcVerdictWarning(file, product);
    if (!warning) {
      updateFlags(file, { [product]: true });
      return;
    }
    setAvailabilityConfirm({ file, product, warning });
  };
  const confirmAvailabilityOverride = () => {
    if (!availabilityConfirm) return;
    const { file, product } = availabilityConfirm;
    setAvailabilityConfirm(null);
    updateFlags(file, { [product]: true });
  };

  // Source-note editor state. `noteEditor` holds either null (closed)
  // or `{ filename, draft }` for the current row being edited. Save
  // sends the empty-string clear signal to the backend if the
  // operator deletes the note's body.
  const [noteEditor, setNoteEditor] = useState(null);
  const openNoteEditor = (file) => {
    setNoteEditor({ filename: file.filename, draft: file.note || '' });
  };
  const saveNote = async () => {
    if (!noteEditor) return;
    const { filename, draft } = noteEditor;
    setNoteEditor(null);
    await updateFlags({ filename }, { note: draft });
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await api.delete(`/api/jobs/available-files/${encodeURIComponent(confirmDelete.filename)}`);
      setConfirmDelete(null);
      refresh();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <PipelineProgress />
      <PageHeader
        title="Sources"
        subtitle="Source files available for Watermark Encode and DCP jobs"
        actions={
          <>
            {isSuperadmin && sortedFiles.length < 10 && (
              <Tooltip title="Operator-only: force show bulk-ops UI on small fixture workspaces">
                <FormControlLabel
                  control={
                    <Switch size="small" checked={bulkForce} onChange={toggleBulkForce} />
                  }
                  label={<Typography variant="caption">Bulk ops</Typography>}
                  sx={{ mr: 1 }}
                />
              </Tooltip>
            )}
            <Tooltip title="Refresh"><IconButton onClick={refresh}><RefreshIcon /></IconButton></Tooltip>
            <input id="source-upload" type="file" hidden multiple onChange={handleUpload} />
            {isGuideDismissed() && (
              <Button size="small" onClick={reopenGuide} sx={{ textTransform: 'none' }}>
                Show upload guide
              </Button>
            )}
            <Button variant="contained" startIcon={<UploadIcon />} onClick={handleUploadClick}>
              Upload Source
            </Button>
          </>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* v11.0.0 — first-time onboarding callout. Dismisses to
          localStorage; once dismissed it stays gone. The audit said
          "a first-timer doesn't know what Watermark Encode or DCP
          jobs are or why they're uploading a 159 GB .mov here." This
          callout sits above the stats so it's the first thing a new
          operator reads on the page. */}
      <SourcesOnboardingCallout />

      {(() => {
        // Aggregate stat row above the table. Tells the operator at
        // a glance "I have N sources totaling X TB; M ready for
        // watermarks, K ready for DCP, J need attention." The ready-
        // for counts respect both the QC verdict and the manual
        // per-product toggle, so a source the operator turned off
        // for DCP doesn't count toward "ready for DCP."
        const a = computeSourceAggregates(sortedFiles);
        if (a.count === 0) return null;
        return (
          <Stack
            direction="row"
            spacing={3}
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">Sources</Typography>
              <Typography variant="h6">{a.count}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Total size</Typography>
              <Typography variant="h6">{formatBytes(a.totalBytes)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Ready for Watermarks</Typography>
              <Typography variant="h6" color="success.main">{a.readyForWatermarks}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Ready for DCP</Typography>
              <Typography variant="h6" color="success.main">{a.readyForDcp}</Typography>
            </Box>
            {a.needsAttention > 0 && (
              <Tooltip title="Click to filter to sources that need attention (fail / needs input).">
                <Box
                  data-testid="sources-needs-attention-tile"
                  role="button"
                  tabIndex={0}
                  onClick={() => setComplianceFilter('needs_input')}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setComplianceFilter('needs_input'); }}
                  sx={{
                    cursor: 'pointer',
                    px: 1, py: 0.25, mx: -1, my: -0.25,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Typography variant="caption" color="text.secondary">Needs attention</Typography>
                  <Typography variant="h6" color="warning.main">{a.needsAttention}</Typography>
                </Box>
              </Tooltip>
            )}
            {a.running > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">QC running</Typography>
                <Typography variant="h6" color="info.main">{a.running}</Typography>
              </Box>
            )}
            {a.bestCount > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">Marked best</Typography>
                <Typography variant="h6" color="warning.main">{a.bestCount}</Typography>
              </Box>
            )}
          </Stack>
        );
      })()}

      {/* Filter toolbar — visible whenever there's at least one
          source. Hidden on an empty workspace because the empty-state
          message inside the table already covers what to do next. */}
      {sortedFiles.length > 0 && (
        <Stack direction="row" spacing={1.5} sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Filter by filename…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 240, flex: 1, maxWidth: 380 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Compliance</InputLabel>
            <Select
              label="Compliance"
              value={complianceFilter}
              onChange={(e) => setComplianceFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pass">Pass only</MenuItem>
              <MenuItem value="warn">Warnings</MenuItem>
              <MenuItem value="fail">Failed</MenuItem>
              <MenuItem value="needs_input">Needs input</MenuItem>
              <MenuItem value="running">QC running</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Available for</InputLabel>
            <Select
              label="Available for"
              value={availableForFilter}
              onChange={(e) => setAvailableForFilter(e.target.value)}
            >
              <MenuItem value="all">Either</MenuItem>
              <MenuItem value="watermarks">Watermarks</MenuItem>
              <MenuItem value="dcp">DCP</MenuItem>
            </Select>
          </FormControl>
          {filtersActive && (
            <>
              <Typography variant="caption" color="text.secondary">
                Showing {filteredFiles.length} of {sortedFiles.length}
              </Typography>
              <Button size="small" onClick={clearFilters}>Clear</Button>
            </>
          )}
        </Stack>
      )}

      {/* Bulk action bar — only renders when bulk ops are visible AND
          at least one row is selected. Operator can delete, re-run
          QC, or flip per-product Available For for the whole batch
          in one click. Concurrency-bounded to 4 at a time so we
          don't fan out 50 parallel calls at the backend. */}
      {bulkVisible && (
        <BulkActionBar
          selectedCount={selectedFilenames.size}
          busy={bulk.busy}
          progress={bulk.progress}
          onClear={bulk.clearSelection}
          actions={
            <>
              <Button size="small" disabled={bulk.busy} onClick={doBulkRerunQc}>Re-run QC</Button>
              <Button size="small" disabled={bulk.busy} onClick={() => doBulkToggleFlag('watermarks', true)}>WM on</Button>
              <Button size="small" disabled={bulk.busy} onClick={() => doBulkToggleFlag('watermarks', false)}>WM off</Button>
              <Button size="small" disabled={bulk.busy} onClick={() => doBulkToggleFlag('dcp', true)}>DCP on</Button>
              <Button size="small" disabled={bulk.busy} onClick={() => doBulkToggleFlag('dcp', false)}>DCP off</Button>
              <Button size="small" color="error" variant="contained" disabled={bulk.busy}
                onClick={() => setBulkDeleteOpen(true)}>Delete</Button>
            </>
          }
        />
      )}

      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 220px)' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {bulkVisible && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={bulk.someVisibleSelected(visibleIds) && !bulk.allVisibleSelected(visibleIds)}
                    checked={bulk.allVisibleSelected(visibleIds)}
                    onChange={() => bulk.toggleSelectAllVisible(visibleIds)}
                    disabled={filteredFiles.length === 0}
                  />
                </TableCell>
              )}
              <TableCell><TableSortLabel {...sortProps('filename')}>Filename</TableSortLabel></TableCell>
              <TableCell sx={{ width: 140 }}><TableSortLabel {...sortProps('qc_compliance')}>Compliance</TableSortLabel></TableCell>
              <TableCell sx={{ width: 100 }}><TableSortLabel {...sortProps('size')}>Size</TableSortLabel></TableCell>
              <TableCell sx={{ width: 170 }}><TableSortLabel {...sortProps('modified')}>Modified</TableSortLabel></TableCell>
              <TableCell sx={{ width: 220 }}>Available For</TableCell>
              <TableCell align="right" sx={{ width: 80 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFiles.map((f) => (
              <TableRow key={f.filename} hover selected={selectedFilenames.has(f.filename)}>
                {bulkVisible && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedFilenames.has(f.filename)}
                      onChange={() => bulk.toggleSelected(f.filename)}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={f.is_best
                      ? 'Marked as best — click to unmark'
                      : 'Mark as best source (operator preferred cut)'}>
                      <IconButton
                        size="small"
                        onClick={() => toggleBest(f)}
                        sx={{ color: f.is_best ? 'warning.main' : 'text.disabled', p: 0.25 }}
                      >
                        {f.is_best ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={f.note || 'Add a note (e.g. "L+R+M cut, not international")'}>
                      <IconButton
                        size="small"
                        onClick={() => openNoteEditor(f)}
                        sx={{ color: f.note ? 'info.main' : 'text.disabled', p: 0.25 }}
                      >
                        {f.note ? <NoteIcon fontSize="small" /> : <NoteOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{f.filename}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {f.qc_running ? (
                    <Tooltip title="Compliance check in progress — click to view current report and live status">
                      <Chip
                        icon={<CircularProgress size={12} sx={{ color: 'inherit !important' }} />}
                        label="RUNNING"
                        size="small"
                        color="info"
                        variant="filled"
                        onClick={() => openQc(f)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </Tooltip>
                  ) : f.qc_compliance ? (
                    // Per-product compliance pills. Sources are used for
                    // BOTH watermarks and DCP, so a DCP-only nit (loudness
                    // off-target, missing FFEC, etc.) shouldn't get
                    // surfaced as a single combined "FAIL" — that mislabels
                    // a streaming-fine source. Two side-by-side pills
                    // make the per-product verdict explicit. Click either
                    // to open the same drawer with the full report.
                    (() => {
                      const cpp = f.qc_compliance_per_product || {
                        watermarks: f.qc_compliance,
                        dcp: f.qc_compliance,
                      };
                      const STATUS_MAP = COMPLIANCE_STATUS_MAP;
                      const tooltip = (product, status) => {
                        if (!status) return `${product}: not yet checked`;
                        const labels = { pass: 'pass', warn: 'warnings', fail: 'fail', needs_input: 'needs input' };
                        return `${product}: ${labels[status] || status} — click for details`;
                      };
                      return (
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title={tooltip('Watermarks', cpp.watermarks)}>
                            <Chip
                              label={`WM ${STATUS_MAP[cpp.watermarks]?.label || '·'}`}
                              size="small"
                              color={STATUS_MAP[cpp.watermarks]?.color || 'default'}
                              variant={STATUS_MAP[cpp.watermarks]?.variant || 'outlined'}
                              onClick={() => openQc(f)}
                              sx={{ cursor: 'pointer', fontSize: '0.65rem', height: 20 }}
                            />
                          </Tooltip>
                          <Tooltip title={tooltip('DCP', cpp.dcp)}>
                            <Chip
                              label={`DCP ${STATUS_MAP[cpp.dcp]?.label || '·'}`}
                              size="small"
                              color={STATUS_MAP[cpp.dcp]?.color || 'default'}
                              variant={STATUS_MAP[cpp.dcp]?.variant || 'outlined'}
                              onClick={() => openQc(f)}
                              sx={{ cursor: 'pointer', fontSize: '0.65rem', height: 20 }}
                            />
                          </Tooltip>
                        </Stack>
                      );
                    })()
                  ) : (
                    <Tooltip title="Run compliance check">
                      <Chip label="Check" size="small" variant="outlined" onClick={() => rerunQc(f)} sx={{ cursor: 'pointer' }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>{fmtBytes(f.size)}</TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">{fmtDate(f.modified)}</Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    {(() => {
                      const wmWarn = qcVerdictWarning(f, 'watermarks');
                      const dcpWarn = qcVerdictWarning(f, 'dcp');
                      return (
                        <>
                          <Tooltip title={wmWarn ? `QC says ${wmWarn} for Watermarks — overriding will encode anyway` : ''}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  color={wmWarn ? 'warning' : 'primary'}
                                  checked={f.available_for_watermark !== false}
                                  onChange={(e) => requestToggleAvailability(f, 'watermarks', e.target.checked)}
                                />
                              }
                              label={<Typography variant="caption">Watermarks</Typography>}
                            />
                          </Tooltip>
                          <Tooltip title={dcpWarn ? `QC says ${dcpWarn} for DCP — overriding will encode anyway` : ''}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  color={dcpWarn ? 'warning' : 'primary'}
                                  checked={f.available_for_dcp !== false}
                                  onChange={(e) => requestToggleAvailability(f, 'dcp', e.target.checked)}
                                />
                              }
                              label={<Typography variant="caption">DCP</Typography>}
                            />
                          </Tooltip>
                        </>
                      );
                    })()}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Download">
                    <IconButton size="small" onClick={async () => {
                      // Mint a one-shot download token, then navigate.
                      // Replaces the legacy ?token=<session-JWT> path
                      // that put the operator's full session credential
                      // into URLs / nginx logs / browser history.
                      try {
                        const { data } = await api.post(
                          `/api/jobs/available-files/${encodeURIComponent(f.filename)}/download-token`
                        );
                        const a = document.createElement('a');
                        a.href = data.url;
                        a.download = f.filename;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                      } catch (e) {
                        setError(e.response?.data?.detail || 'Download failed');
                      }
                    }}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => setConfirmDelete(f)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {filteredFiles.length === 0 && sortedFiles.length === 0 && (
              <TableRow>
                <TableCell colSpan={bulkVisible ? 7 : 6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No source files yet.</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click "Upload Source" to add a mezzanine or master file.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filteredFiles.length === 0 && sortedFiles.length > 0 && (
              <TableRow>
                <TableCell colSpan={bulkVisible ? 7 : 6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No sources match the current filter.
                  </Typography>
                  <Button size="small" onClick={clearFilters} sx={{ mt: 1 }}>
                    Clear filters
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={uploadGuide} onClose={() => setUploadGuide(false)} maxWidth="md" fullWidth>
        <DialogTitle>Before you upload, what we need from your source</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Source files are commonly 50 to 500 GB and uploads can take hours. A few minutes
            spent here saves a re-upload later. Confirm each item with your post team and
            check the boxes to continue.
          </Alert>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>How our pre-flight works</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            We check your source in two passes:
          </Typography>
          <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight={600} sx={{ color: 'success.main', mb: 0.5 }}>
              ⚡ The instant your file is picked, before any byte uploads
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Your browser reads the file's container header locally, a few hundred kilobytes
              even if the file itself is 500 GB, and checks codec, container, resolution,
              frame rate, embedded timecode, audio channel count, sample rate, and bit depth.
              If anything is wrong with the structure (HEVC long-GOP, variable frame rate, no audio
              stream, missing video, etc.), we tell you immediately and the upload never starts.
              This catches the worst failures in 2 to 10 seconds with zero wasted bandwidth.
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ color: 'info.main', mt: 1, mb: 0.5 }}>
              🛡️ As the first 64 MB lands on our server
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              The same header is verified server-side as a safety net. If the first chunk
              fails to parse, we abort the rest of the upload and clean up, so the worst case
              even with browser sniff disabled is ~64 MB of wasted transfer instead of all 500 GB.
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ color: 'warning.main', mt: 1, mb: 0.5 }}>
              🔍 After the upload finishes
            </Typography>
            <Typography variant="body2">
              The full file is decoded for the deeper checks that need every frame and every
              audio sample: head/tail black detection, automatic FFEC / FFMC marker discovery,
              integrated audio loudness, and true peak measurement. These take a minute or two
              and surface as a green / yellow / red compliance badge on your source row, with a
              detailed drilldown drawer. None of them block your encode, they're informational,
              so you and your team know exactly what to fix before show day.
            </Typography>
          </Box>

          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Things we cannot detect from the file itself, even after a full decode pass:</strong>
            audio mix decisions (incorrect channel routing, wrong language stem, surround channels
            mapped to the wrong speakers), creative color grade issues, missing reels, or whether
            the cut you've delivered is actually the cut your director approved. For those,
            ask your audio team for a <strong>QC report from their mix stage</strong> and
            confirm with your colorist and editor that the file you're uploading is the
            current locked master. We cannot QC for you what only your team can verify.
          </Alert>

          <Alert severity="success" sx={{ mb: 2 }}>
            <strong>Resumable uploads:</strong> the upload protocol is fully resumable. If your
            connection drops, your laptop sleeps, or you close the tab by accident, your transfer
            picks up exactly where it left off when you come back, even from a different device.
            No more re-uploading 200 GB because of a single dropped packet.
          </Alert>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>1. Picture master</Typography>
          <Box component="ul" sx={{ pl: 3, my: 1 }}>
            <li><Typography variant="body2"><strong>Codec:</strong> ProRes 422 HQ or DNxHR HQ are ideal. Uncompressed and intermediate codecs also work. Long-GOP H.264 / HEVC will encode (and we'll warn you), but it's not a true master and quality will reflect that.</Typography></li>
            <li><Typography variant="body2"><strong>Color space:</strong> SDR, Rec.709, full or limited range, both are handled. We do not currently process HDR (Dolby Vision, HDR10, EclairColor) sources.</Typography></li>
            <li><Typography variant="body2"><strong>Resolution:</strong> 1920×1080 (HD) or higher. 2K (2048×1080) and 4K (4096×2160) DCI are best. We'll upscale to the nearest DCI container if needed.</Typography></li>
            <li><Typography variant="body2"><strong>Frame rate:</strong> 23.976, 24, 25, 30, 48, or 60 fps. The rate must be constant, variable frame rate sources from screen recorders or smartphones will produce drift in the final DCP.</Typography></li>
            <li><Typography variant="body2"><strong>Aspect ratio:</strong> Pillar/letterbox in the master is fine. We container to DCI Flat (1.85) or DCI Scope (2.39) at encode time.</Typography></li>
          </Box>
          <FormControlLabel sx={{ mt: 0.5 }}
            control={<Checkbox checked={uploadConfirmed.master} onChange={(e) => setUploadConfirmed(c => ({ ...c, master: e.target.checked }))} />}
            label={<Typography variant="body2">Confirmed with my post team that the picture master meets the above.</Typography>}
          />

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, color: 'primary.main' }}>2. Embedded timecode and leader</Typography>
          <Box component="ul" sx={{ pl: 3, my: 1 }}>
            <li><Typography variant="body2"><strong>The standard:</strong> the master should carry embedded timecode and the first frame of action should land on <strong>01:00:00:00</strong>. Bars and tone, slate, and countdown live in the half-minute before that, with the timecode track typically starting at 00:59:30:00.</Typography></li>
            <li><Typography variant="body2"><strong>Why it matters:</strong> the 01:00:00:00 convention lets us anchor every other timecode reference (FFEC, FFMC, reel breaks) reliably. Studio QC desks expect to see it. Most professional masters already have it.</Typography></li>
            <li><Typography variant="body2"><strong>If your master doesn't:</strong> that's okay. We'll still encode it. You'll see a warning on the compliance report and you can either re-upload a corrected version or proceed as-is. We won't reject the file.</Typography></li>
            <li><Typography variant="body2"><strong>What to avoid:</strong> masters that start at 00:00:00:00 with picture on frame 1, or masters with no embedded timecode at all. They encode but they make troubleshooting harder.</Typography></li>
          </Box>
          <FormControlLabel sx={{ mt: 0.5 }}
            control={<Checkbox checked={uploadConfirmed.time} onChange={(e) => setUploadConfirmed(c => ({ ...c, time: e.target.checked }))} />}
            label={<Typography variant="body2">Asked my post team about the embedded timecode and leader convention.</Typography>}
          />

          {/* FFEC/FFMC markers are a DCP-only requirement. If the
              operator is currently working on the watermarks side
              (`activeProduct === 'press_review'`), this whole section
              is irrelevant and just adds noise to the wall of text.
              We keep it visible when the active product is unset (the
              workspace doesn't know yet) or theatrical_dcp. */}
          {shouldShowFfecSection(activeProduct) && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, color: 'primary.main' }}>3. End-credits markers (features only)</Typography>
              <Box component="ul" sx={{ pl: 3, my: 1 }}>
                <li><Typography variant="body2"><strong>What we need:</strong> SMPTE Bv2.1 features must carry two markers, <strong>FFEC</strong> (First Frame of End Credits, the cut from picture to credits) and <strong>FFMC</strong> (First Frame of Moving Credits, when the credit roll starts scrolling). Without these, the DCP fails QC.</Typography></li>
                <li><Typography variant="body2"><strong>How we find them:</strong> we look for a clean cut to black before the credit roll. If there's even a half-second of black between the last picture frame and the first credit, we'll find it automatically.</Typography></li>
                <li><Typography variant="body2"><strong>If we can't find them:</strong> the compliance report will say "needs input" and ask you to either type the FFEC/FFMC timecodes by hand, or have your post team add a 1-second black gap between the last picture frame and the start of the credit roll and re-upload.</Typography></li>
                <li><Typography variant="body2"><strong>For non-features</strong> (shorts, trailers, advertisements), this section doesn't apply, those content types don't require the markers.</Typography></li>
              </Box>
            </>
          )}

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, color: 'primary.main' }}>4. Audio</Typography>
          <Box component="ul" sx={{ pl: 3, my: 1 }}>
            <li><Typography variant="body2"><strong>Layout:</strong> we accept stereo (2.0) and 5.1 surround. The 5.1 channel order should be L, R, C, LFE, Ls, Rs (industry standard). Anything else (7.1, immersive, Dolby Atmos) is not currently in our pipeline.</Typography></li>
            <li><Typography variant="body2"><strong>Loudness:</strong> theatrical mixes target around <strong>-27 LUFS integrated</strong>, true peaks at or below <strong>-2 dBTP</strong>. The compliance check measures both. Loud or quiet outside that window encodes fine but we'll flag it for your awareness.</Typography></li>
            <li><Typography variant="body2"><strong>Bit depth and sample rate:</strong> 24-bit / 48 kHz is standard. 16-bit / 48 kHz also works. We'll resample if needed.</Typography></li>
            <li><Typography variant="body2"><strong>Embedded vs separate:</strong> embedded audio in the same file is preferred. Sidecar WAV / BWF stems are not currently supported through self-service upload.</Typography></li>
          </Box>
          <FormControlLabel sx={{ mt: 0.5 }}
            control={<Checkbox checked={uploadConfirmed.audio} onChange={(e) => setUploadConfirmed(c => ({ ...c, audio: e.target.checked }))} />}
            label={<Typography variant="body2">Confirmed audio layout, loudness target, and sample rate with my post team.</Typography>}
          />

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, color: 'primary.main' }}>5. Subtitles, captions, and other deliverables</Typography>
          <Box component="ul" sx={{ pl: 3, my: 1 }}>
            <li><Typography variant="body2">We do not currently ingest subtitle, caption, or accessibility tracks through self-service. If your screening requires them, get in touch separately.</Typography></li>
            <li><Typography variant="body2">Multiple language audio versions, supplemental packages (VF / VL), and IMF deliveries are not in the self-service lane either.</Typography></li>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>6. Practical upload notes</Typography>
          <Box component="ul" sx={{ pl: 3, my: 1 }}>
            <li><Typography variant="body2"><strong>Wired connection:</strong> use ethernet, not Wi-Fi, for anything over a few GB. Wi-Fi flakiness mid-upload is the most common cause of failed transfers.</Typography></li>
            <li><Typography variant="body2"><strong>Don't close the tab:</strong> uploads run in the floating overlay at bottom-right and survive page navigation, but they need the tab to stay open. Closing the tab pauses the transfer.</Typography></li>
            <li><Typography variant="body2"><strong>You can keep working:</strong> open another tab, browse the rest of the app, create jobs from existing sources, the upload runs in the background.</Typography></li>
            <li><Typography variant="body2"><strong>Upload speed reality check:</strong> a 100 GB file at 100 Mbps takes about 2.5 hours. At 1 Gbps it's about 15 minutes. If your symmetric upload is slower than your download, plan accordingly.</Typography></li>
            <li><Typography variant="body2"><strong>Cancellation:</strong> you can cancel an in-progress upload from the floating overlay. The partial file is cleaned up automatically.</Typography></li>
            <li><Typography variant="body2"><strong>Re-uploading:</strong> if you upload a file with a name that already exists, we automatically append <code>_(2)</code>, <code>_(3)</code>, etc. so we never silently overwrite an existing source.</Typography></li>
          </Box>
          <FormControlLabel sx={{ mt: 0.5 }}
            control={<Checkbox checked={uploadConfirmed.codec} onChange={(e) => setUploadConfirmed(c => ({ ...c, codec: e.target.checked }))} />}
            label={<Typography variant="body2">Understood. I have a stable connection and I know the upload may take a while.</Typography>}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <FormControlLabel
            sx={{ ml: 0 }}
            control={
              <Checkbox
                size="small"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                Don't show this guide every upload (you can reopen it from "Show upload guide")
              </Typography>
            }
          />
          <Box>
            <Button onClick={() => setUploadGuide(false)}>Cancel</Button>
            <Button variant="contained" disabled={!allConfirmed} onClick={triggerFilePicker}>
              Choose file{allConfirmed ? '' : `, ${4 - Object.values(uploadConfirmed).filter(Boolean).length} confirmation${(4 - Object.values(uploadConfirmed).filter(Boolean).length) === 1 ? '' : 's'} remaining`}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog open={sniffing} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 5 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body2">Reading the file header in your browser… (no bytes uploaded yet)</Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sniffPending} onClose={() => sniffPending?.cancel?.()} maxWidth="md" fullWidth>
        <DialogTitle>
          {sniffPending?.blocked ? 'Source rejected by pre-flight' : 'Pre-flight results'}
        </DialogTitle>
        <DialogContent dividers>
          {sniffPending && (
            <Box>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
                {sniffPending.file.name} · {fmtBytes(sniffPending.file.size)}
              </Typography>
              <Chip
                label={`${(sniffPending.sniff.compliance || 'unknown').toUpperCase()}, instant browser check`}
                color={sniffPending.sniff.compliance === 'pass' ? 'success' : sniffPending.sniff.compliance === 'warn' ? 'warning' : 'error'}
                sx={{ mb: 2 }}
              />
              <Stack spacing={1}>
                {(sniffPending.sniff.checks || []).map((c, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Chip size="small"
                      label={c.status.toUpperCase()}
                      color={c.status === 'pass' ? 'success' : c.status === 'warn' ? 'warning' : 'error'}
                      sx={{ minWidth: 80 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{c.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{c.message}</Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>

              {sniffPending.blocked ? (
                <Alert severity="error" sx={{ mt: 3 }}>
                  This source cannot be uploaded as-is. Fix the issues above and try again.
                  No bytes have been uploaded, your bandwidth is preserved.
                </Alert>
              ) : sniffPending.sniff.compliance === 'warn' ? (
                <Alert severity="warning" sx={{ mt: 3 }}>
                  We found warnings but the source is technically usable. You can proceed with
                  the upload, the deeper checks (head/tail black, FFEC marker discovery,
                  loudness, true peaks) will run after the upload finishes.
                </Alert>
              ) : (
                <Alert severity="success" sx={{ mt: 3 }}>
                  All instant checks passed. The deeper post-upload checks will run automatically
                  after the file finishes uploading.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => sniffPending?.cancel?.()}>
            {sniffPending?.blocked ? 'Close' : 'Cancel'}
          </Button>
          {!sniffPending?.blocked && (
            <Button variant="contained" onClick={() => sniffPending?.confirm?.()}>
              Start upload
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete source file?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Permanently delete <strong>{confirmDelete?.filename}</strong>?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            This removes the file from your workspace's source directory. Active
            jobs that reference it will block deletion.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={deleting} onClick={doDelete}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!availabilityConfirm} onClose={() => setAvailabilityConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Override QC verdict?</DialogTitle>
        <DialogContent>
          {availabilityConfirm && (() => {
            const { file, product, warning } = availabilityConfirm;
            const productLabel = product === 'watermarks' ? 'Watermarks' : 'DCP';
            const verdictLabel = warning === 'fail' ? 'fail'
              : warning === 'needs_input' ? 'needs input (FFEC/FFMC missing)'
              : warning;
            return (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Pre-flight QC says <strong>{verdictLabel}</strong> for <strong>{productLabel}</strong>.
                </Alert>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                  {file.filename}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Turning {productLabel} ON for this source means jobs created
                  against it can still proceed. {warning === 'fail' && 'The encoder may produce artifacts or fail outright.'}
                  {warning === 'needs_input' && 'You\'ll need to supply FFEC/FFMC markers manually before the DCP can be built.'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  If your source has been replaced or fixed, click "Re-run QC" in the
                  compliance drawer first; this confirmation is for genuine overrides only.
                </Typography>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAvailabilityConfirm(null)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={confirmAvailabilityOverride}>
            Override
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!noteEditor} onClose={() => setNoteEditor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Source note</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontFamily: 'monospace' }}>
            {noteEditor?.filename}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            inputProps={{ maxLength: 1024 }}
            placeholder='e.g. "L+R+M only, not international" or "festival cut, not theatrical"'
            value={noteEditor?.draft || ''}
            onChange={(e) => setNoteEditor((prev) => ({ ...prev, draft: e.target.value }))}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Up to 1,024 characters. Empty saves as no note.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteEditor(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveNote}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDeleteOpen} onClose={() => setBulkDeleteOpen(false)}>
        <DialogTitle>Delete {selectedFilenames.size} source files?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Permanently delete <strong>{selectedFilenames.size}</strong> files?
            Any with active jobs referencing them will be skipped — others will
            be removed from this workspace's source directory.
          </Typography>
          <Box sx={{ mt: 2, p: 1.5, maxHeight: 200, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {Array.from(selectedFilenames).slice(0, 50).map((fn) => (
              <Typography key={fn} variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                {fn}
              </Typography>
            ))}
            {selectedFilenames.size > 50 && (
              <Typography variant="caption" color="text.secondary">
                …and {selectedFilenames.size - 50} more
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={doBulkDelete}>
            Delete {selectedFilenames.size}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer anchor="right" open={!!qcDrawer} onClose={() => setQcDrawer(null)}
        PaperProps={{ sx: { width: { xs: '100vw', md: 720 }, p: 3 } }}>
        {qcDrawer && (
          <Box>
            <Typography variant="h6">Source Compliance, {qcDrawer.filename}</Typography>
            <Typography variant="caption" color="text.secondary">
              Pre-flight checks run automatically when a source uploads.
            </Typography>
            {/* Note from the row, surfaced inline. Click to edit. */}
            {(() => {
              const f = (availableFiles || []).find((x) => x.filename === qcDrawer.filename);
              if (!f) return null;
              return (
                <Box
                  sx={{
                    mt: 1.5, p: 1.5, borderRadius: 1,
                    border: '1px dashed', borderColor: f.note ? 'info.main' : 'divider',
                    cursor: 'pointer',
                  }}
                  onClick={() => openNoteEditor(f)}
                >
                  <Typography variant="caption" color={f.note ? 'info.main' : 'text.secondary'}>
                    {f.note ? 'Note' : 'Add a note'}
                  </Typography>
                  {f.note && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                      {f.note}
                    </Typography>
                  )}
                </Box>
              );
            })()}
            {qcDrawer.loading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>}
            {qcDrawer.error && <Alert severity="error" sx={{ mt: 2 }}>{qcDrawer.error}</Alert>}
            {/* Running indicator. Sits above the partial report so the
                operator can see what's already been computed (early
                checks finish first) while later phases — black-detect,
                FFEC search, loudness — keep running. Indeterminate bar
                because the backend doesn't expose phase progress, but
                naming the active phase would be a nice follow-up. */}
            {qcDrawer.running && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>QC in progress…</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Black-frame detection, FFEC search, and loudness measurement can take 1–5 minutes on a feature.
                  </Typography>
                </Box>
                <LinearProgress />
              </Box>
            )}
            {qcDrawer.report && (
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={qcDrawer.running
                    ? 'Compliance: still computing…'
                    : `Compliance: ${qcDrawer.report.compliance?.toUpperCase() || 'UNKNOWN'}`}
                  color={qcDrawer.running ? 'info' : complianceChipColor(qcDrawer.report.compliance)}
                />
                <Box sx={{ float: 'right' }}>
                  <Button size="small" disabled={qcDrawer.running} onClick={() => rerunQc({ filename: qcDrawer.filename })}>
                    {qcDrawer.running ? 'Running…' : 'Re-run'}
                  </Button>
                </Box>

                {/* Create-job CTAs. The whole point of QC passing is
                    "go encode this thing" — without these the operator
                    has to close the drawer, navigate away, re-pick the
                    file. We surface both Watermarks and DCP buttons,
                    each independently gated by their per-product
                    verdict + Available For toggle. */}
                {(() => {
                  const f = (availableFiles || []).find((x) => x.filename === qcDrawer.filename);
                  const flags = {
                    watermarks: f ? f.available_for_watermark !== false : true,
                    dcp:        f ? f.available_for_dcp        !== false : true,
                  };
                  const wmOk  = canCreateForProduct(qcDrawer.report, 'watermarks', flags);
                  const dcpOk = canCreateForProduct(qcDrawer.report, 'dcp', flags);
                  if (qcDrawer.running) return null;
                  if (!wmOk && !dcpOk) return null;
                  const goCreate = (path) => {
                    setQcDrawer(null);
                    navigate(`${path}?source=${encodeURIComponent(qcDrawer.filename)}`);
                  };
                  return (
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mb: 0.5 }}>
                        Use this source:
                      </Typography>
                      {wmOk && (
                        <Button size="small" variant="contained" color="primary"
                          onClick={() => goCreate('/screeners/create')}>
                          Create Watermark
                        </Button>
                      )}
                      {dcpOk && (
                        <Button size="small" variant="contained" color="primary"
                          onClick={() => goCreate('/dcp/create')}>
                          Create DCP
                        </Button>
                      )}
                    </Box>
                  );
                })()}

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Findings</Typography>
                <Stack spacing={1}>
                  {(qcDrawer.report.checks || []).map((c, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <Chip size="small" label={c.status.toUpperCase()} color={complianceChipColor(c.status)} sx={{ minWidth: 90 }} />
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{c.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{c.message}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>

                {qcDrawer.report.compliance === 'needs_input' && (
                  <Box sx={{ mt: 3, p: 2, border: '1px dashed', borderColor: 'warning.main', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="warning.main" gutterBottom>
                      <WarnIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                      We need your help with FFEC/FFMC
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      A SMPTE Bv2.1 Feature DCP requires two markers: <strong>FFEC</strong> (First
                      Frame of End Credits) and <strong>FFMC</strong> (First Frame of Moving Credits).
                      Our auto-detector couldn't find a clear cut between picture and credits in your
                      source. Either supply the timecodes here, or send the message below to your post
                      team and re-upload the corrected source.
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                      <TextField size="small" label="FFEC timecode (HH:MM:SS:FF)"
                        value={manualMarkers.ffec_timecode}
                        onChange={(e) => setManualMarkers(m => ({ ...m, ffec_timecode: e.target.value }))} />
                      <TextField size="small" label="FFMC timecode (HH:MM:SS:FF)"
                        value={manualMarkers.ffmc_timecode}
                        onChange={(e) => setManualMarkers(m => ({ ...m, ffmc_timecode: e.target.value }))} />
                      <Button variant="contained" size="small" onClick={submitManualMarkers}>Save</Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Timecodes should reference the source's embedded timecode (typically starting
                      at 01:00:00:00 for masters with bars/tone at 00:59:30:00).
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Master compliance reference</Typography>
                <Box component="ul" sx={{ pl: 2, m: 0, '& li': { mb: 0.5 } }}>
                  <li><Typography variant="caption">Embedded timecode starts at <strong>01:00:00:00</strong> (one hour), bars/tone at <strong>00:59:30:00</strong></Typography></li>
                  <li><Typography variant="caption">A few seconds of <strong>head black</strong> before first picture frame</Typography></li>
                  <li><Typography variant="caption">A clean cut to <strong>black before end credits</strong> (this is FFEC)</Typography></li>
                  <li><Typography variant="caption">Credits start scrolling <strong>~5 seconds after FFEC</strong> (this is FFMC)</Typography></li>
                  <li><Typography variant="caption">A few seconds of <strong>tail black</strong> after the last credit</Typography></li>
                  <li><Typography variant="caption">Codec: <strong>ProRes 422 HQ</strong> or <strong>DNxHR HQ</strong> (avoid long-GOP H.264 masters)</Typography></li>
                  <li><Typography variant="caption">Frame rate: 23.976, 24, 25, or 30 fps</Typography></li>
                  <li><Typography variant="caption">Audio: 5.1 surround (L R C LFE Ls Rs) or stereo, integrated loudness around <strong>-27 LUFS</strong>, true peaks at or below <strong>-2 dBTP</strong></Typography></li>
                </Box>

                {qcDrawer.report.metadata && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Source metadata</Typography>
                    <Typography variant="caption" component="div">
                      Codec: {qcDrawer.report.metadata.codec} ({qcDrawer.report.metadata.container})<br/>
                      Resolution: {qcDrawer.report.metadata.width}x{qcDrawer.report.metadata.height} @ {Number(qcDrawer.report.metadata.fps).toFixed(3)} fps<br/>
                      Duration: {Math.round(qcDrawer.report.metadata.duration_seconds)}s<br/>
                      Start TC: {qcDrawer.report.metadata.start_timecode || '(none)'}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
